import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  Stack,
  Autocomplete,
  Button,
  Divider,
  TextField,
  alpha,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import { ThemeProvider, Icon, Message } from '@cx/ui'
import { Decimal } from 'decimal.js'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { grayBg } from '@/constant'
import { LoadingButton } from '@mui/lab'
import { useDebounceFn, useRequest } from 'ahooks'
import {
  cancelOrder,
  getOrderDetail,
  getCouponCode,
  getReferralCode,
  postUploadPayReceipt,
  getPaymentUrl,
  voucherCodePurchase,
  postOrder0Pay,
  postOrderConfirm,
  getCoupons,
} from '@/api'
import DownloadContractModal from './DownloadContractModal'
import { OrderGoodsDetail, OrderStatusEnum, OrderPayTypeEnum } from '@/types'
import ApplyInvoiceModal from './ApplyInvoiceModal'
import ReceiptModal from './ReceiptModal'
import { Card, Text3, Text, VisuallyHiddenInput, SerialText } from '../common'
import { getDurationString } from './utils'
import PayeeModal from './PayeeModal'
import { PAY_TYPE_TO_TEXT } from './OrderCard'
import Modal2 from '@/components/Modal2'

import theme from '@/theme2'

export enum TabValue {
  Space,
  Order,
}

const formatGoodsDetails = (detail: string) => {
  const durations = { D: '日', M: '月', Y: '年' }
  let goodsDetail
  try {
    goodsDetail = JSON.parse(detail) as OrderGoodsDetail
  } catch (e) {}
  if (goodsDetail) {
    const details = [
      {
        name: goodsDetail?.package_name,
        value: `${goodsDetail?.duration || ''} ${durations[goodsDetail?.purchase_duration_unit] || ''}`,
      },
    ]
    goodsDetail?.items?.forEach((item) => details.push(item))
    return details?.map((item) => `${item.name} ${item.value}`)
  } else {
    return ['']
  }
}

const PAY_TYPES = [
  { label: '支付宝', value: OrderPayTypeEnum.alipay, icon: 'icon-zhifubaozhifu' },
  { label: '对公转账', value: OrderPayTypeEnum.public, icon: 'icon-duigongfukuan' },
]

const PAY_STATUS = {
  [OrderStatusEnum.unpaid]: {
    label: '等待支付',
  },
  [OrderStatusEnum.toBeConfirmed]: {
    label: '订单创建成功！',
  },
  [OrderStatusEnum.canceled]: {
    label: '订单已关闭，请重新下单',
  },
  [OrderStatusEnum.expaired]: {
    label: '订单已过期，请重新下单',
  },
  [OrderStatusEnum.auditing]: {
    label: '等待审核对公信息',
  },
  [OrderStatusEnum.paid]: {
    label: '订单支付成功！感谢您的购买',
  },
}

const OrderPay = () => {
  const [payeeModalVisible, setPayeeModalVisible] = useState(false)
  const [receiptModalVisible, setReceiptModalVisible] = useState(false)
  const [downloadContractModalVisible, setDownloadContractModalVisible] = useState(false)
  const [endTime, setEndTime] = useState(-1)
  const [payLoading, setPayLoading] = useState(false)
  const countDownRef = useRef<NodeJS.Timeout>()
  const navigate = useNavigate()
  const [orderDetail, setOrderDetail] = useState<any>({})
  const [payType, setPayType] = useState(1)
  const [applyInvoiceModalVisible, setApplyInvoiceModalVisible] = useState(false)
  const [referral_code, set_referral_code] = useState<string>('')
  const [referralCode, setReferralCode] = useState(referral_code || '')
  const [couponCode, setCouponCode] = useState('')
  const [referralCodeInfo, setReferralCodeInfo] = useState<any>(null)
  const [referralCodeError, setReferralCodeError] = useState('')
  const [couponCodeInfo, setCouponCodeInfo] = useState<any>(null)
  const [couponCodeError, setCouponCodeError] = useState('')
  const [isAgree, setIsAgree] = useState(false)
  const [referralCodeChecked, setReferralCodeChecked] = useState(!!referralCode)
  const [couponCodeChecked, setCouponCodeChecked] = useState(false)
  const [company, setCompany] = useState('')
  const [companyError, setCompanyError] = useState('')

  const [searchParams] = useSearchParams()

  const { data: coupons } = useRequest(() => getCoupons({ order_code: searchParams.get('order_code')! }))

  const getOrderDetailQuery = () => {
    getOrderDetail({ order_code: searchParams.get('order_code')!, org_id: searchParams.get('org_id')! }).then(
      (result) => {
        setOrderDetail(result)
        setEndTime(result.expire_time_seconds)
        countDownRef.current = setInterval(() => {
          if (result.expire_time_seconds > 0) {
            setEndTime((second: number) => {
              if (second <= 1) {
                navigate(0)
                return 0
              } else {
                return second - 1
              }
            })
          } else {
            clearInterval(countDownRef.current)
          }
        }, 1000)
      },
    )
  }

  useEffect(() => {
    getOrderDetailQuery()
  }, [])

  const onCheckReferralCode = (referralCode: string) => {
    if (!referralCode) {
      setReferralCodeInfo(null)
      setReferralCodeError('')
      return
    }
    return getReferralCode({ referral_code: referralCode })
      .then((result) => {
        setReferralCodeInfo(result)
        setReferralCodeError('')
      })
      .catch((err) => {
        setReferralCodeInfo(null)
        setReferralCodeError(err)
      })
  }
  const onCheckCouponCode = (couponCode: string) => {
    if (!couponCode) {
      setCouponCodeInfo(null)
      setCouponCodeError('')
      return
    }
    return getCouponCode({ coupon_code: couponCode, order_code: searchParams.get('order_code')! })
      .then((result) => {
        setCouponCodeInfo(result)
        setCouponCodeError('')
      })
      .catch((err) => {
        setCouponCodeError(err)
        setCouponCodeInfo(null)
      })
  }

  const { run: debounceCheckReferralCode } = useDebounceFn(
    (referralCode: string) => {
      onCheckReferralCode(referralCode)
    },
    {
      wait: 500,
    },
  )

  const { run: debounceCheckCouponCode } = useDebounceFn(
    (couponCode: string) => {
      onCheckCouponCode(couponCode)
    },
    {
      wait: 500,
    },
  )

  const hasDiscountCoupon = useMemo(() => {
    if (
      orderDetail &&
      ((orderDetail.referral_code?.saved_amount || 0) > 0 || (orderDetail.coupon_code?.saved_amount || 0) > 0)
    ) {
      return true
    }
    return false
  }, [orderDetail])

  const onCancelOrder = () => {
    Modal2.confirm({
      title: '提示',
      content: '确认关闭订单？',
      onOk: () =>
        cancelOrder({ order_code: searchParams.get('order_code')! })
          .then(() => {
            navigate(0)
            Message.success('订单关闭成功')
          })
          .catch((e) => {
            navigate(0)
          }),
    })
  }

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const order_code = searchParams.get('order_code')!
    setPayLoading(true)
    postUploadPayReceipt({
      order_code,
      receipt: e.target.files![0]!,
    })
      .then((res) => {
        voucherCodePurchase({
          company: company,
          order_code,
          pay_type: payType,
          referral_code: referralCodeInfo?.referral_code,
          coupon_code: couponCodeInfo?.coupon_code,
          receipt_url: res.receipt_url,
        }).then(() => {
          Message.success('上传成功，请等待审核')
          navigate(0)
        })
      })
      .finally(() => {
        setPayLoading(false)
      })
  }

  const onConfirmOrder = () => {
    if (orderDetail.status === OrderStatusEnum.toBeConfirmed) {
      if (!isAgree) {
        Message.error('请先阅读长亭百川云平台产品服务协议')
        return
      }
      postOrderConfirm({
        order_code: searchParams.get('order_code')!,
        coupon_code: couponCodeChecked ? couponCodeInfo?.coupon_code || '' : '',
        referral_code: referralCodeChecked ? referralCodeInfo?.referral_code || '' : '',
      }).then(() => {
        set_referral_code(referralCodeInfo?.referral_code || '')
        navigate(0)
      })
    }

    if (orderDetail.status === OrderStatusEnum.unpaid) {
      onSubmitPay()
    }
  }

  const onSubmitPay = () => {
    if (orderDetail.real_amount <= 0) {
      postOrder0Pay({
        order_code: searchParams.get('order_code')!,
        coupon_code: orderDetail?.coupon_code?.coupon_code,
      }).then(() => {
        Message.success('支付成功')
        navigate(0)
      })
    } else {
      getPaymentUrl({
        order_code: searchParams.get('order_code')!,
        pay_type: payType,
      }).then((result) => {
        window.open(result.url, '_self')
      })
    }
  }

  const couponCodeMoney = useMemo(() => {
    if (couponCodeInfo) {
      if (couponCodeInfo.kind === 2) {
        if (couponCodeInfo.discount === 1) {
          return 0
        }
        return Decimal.mul(orderDetail?.total_amount || 0, 1 - couponCodeInfo.discount).toFixed(2)
      }
      if (couponCodeInfo.kind === 1) {
        return couponCodeInfo.amount.toFixed(2)
      }
    } else {
      return 0
    }
  }, [couponCodeInfo, orderDetail])

  const referralMoney = useMemo(() => {
    if (referralCodeInfo) {
      if (referralCodeInfo.discount === 1) {
        return 0
      }
      return Decimal.mul(
        couponCodeMoney
          ? Decimal.sub(orderDetail.total_amount || 0, couponCodeMoney || 0)
          : orderDetail?.total_amount || 0,
        1 - referralCodeInfo.discount,
      ).toFixed(2)
    } else {
      return 0
    }
  }, [referralCodeInfo, orderDetail, couponCodeMoney])

  const payMoney = useMemo(() => {
    return couponCodeMoney || referralMoney
      ? Decimal.sub(orderDetail.total_amount || 0, couponCodeMoney || 0)
          .sub(referralMoney || 0)
          .toFixed(2)
      : (orderDetail.total_amount || 0).toFixed(2)
  }, [orderDetail, couponCodeMoney, referralMoney])

  useEffect(() => {
    if (referralCodeChecked) {
      onCheckReferralCode(referralCode)
    }
  }, [referralCodeChecked])

  useEffect(() => {
    if (couponCodeChecked) {
      onCheckCouponCode(couponCode)
    }
  }, [couponCodeChecked])

  useEffect(() => {
    return () => {
      clearInterval(countDownRef.current)
    }
  }, [])

  return (
    <ThemeProvider theme={theme} mode='light'>
      <Box
        sx={{
          pt: 13,
          backgroundColor: grayBg,
          height: '100vh',
          overflowY: 'auto',
          pb: 5,
        }}
      >
        <DownloadContractModal
          open={downloadContractModalVisible}
          onOk={() => setDownloadContractModalVisible(false)}
          onCancel={() => setDownloadContractModalVisible(false)}
          data={orderDetail}
        />
        <ReceiptModal
          url={orderDetail.receipt_url}
          open={receiptModalVisible}
          onCancel={() => setReceiptModalVisible(false)}
        />
        <ApplyInvoiceModal
          open={applyInvoiceModalVisible}
          onCancel={() => {
            setApplyInvoiceModalVisible(false)
          }}
          onOk={() => {
            setApplyInvoiceModalVisible(false)
          }}
          data={orderDetail}
        />
        <PayeeModal
          orderCode={orderDetail.order_code}
          orderAmount={orderDetail.real_amount?.toFixed(2)}
          open={payeeModalVisible}
          onCancel={() => {
            setPayeeModalVisible(false)
          }}
        />
        <Stack gap={3} sx={{ width: 1120, mx: 'auto' }}>
          <Card>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Box sx={{ color: '#000', fontWeight: 'bold', fontSize: 24 }}>
                {PAY_STATUS[orderDetail.status as keyof typeof PAY_STATUS]?.label}
              </Box>
              <Stack direction='row' alignItems='center'>
                {/* {orderDetail.status === OrderStatusEnum.paid && (
                  <>
                    {InvoiceStatusEnum.unApply === orderDetail.invoice.status && (
                      <Button
                        size='small'
                        onClick={() => {
                          setApplyInvoiceModalVisible(true)
                        }}
                      >
                        申请发票
                      </Button>
                    )}
                    {InvoiceStatusEnum.applying === orderDetail.invoice.status && (
                      <Button size='small' disabled>
                        发票申请中
                      </Button>
                    )}
                    {InvoiceStatusEnum.applied === orderDetail.invoice.status && (
                      <Button size='small' href={orderDetail.invoice.invoice_url}>
                        下载发票
                      </Button>
                    )}
                  </>
                )} */}

                {orderDetail.has_contract &&
                  [OrderStatusEnum.unpaid, OrderStatusEnum.paid, OrderStatusEnum.auditing].includes(
                    orderDetail.status,
                  ) && (
                    <Button
                      size='small'
                      onClick={() => {
                        setDownloadContractModalVisible(true)
                      }}
                    >
                      下载合同
                    </Button>
                  )}
              </Stack>
            </Stack>
            {orderDetail.status === OrderStatusEnum.unpaid && (
              <Stack direction='row' alignItems='center' gap={0.5} sx={{ fontSize: 12, mt: 1 }}>
                <ErrorRoundedIcon sx={{ color: 'error.main', fontSize: 16 }} />
                <Box>
                  支付剩余{' '}
                  <Box component='span' sx={{ color: 'error.main' }}>
                    {getDurationString(endTime)}
                  </Box>
                  ，否则订单自动关闭
                </Box>
              </Stack>
            )}

            <Divider sx={{ borderColor: '#eee', my: 3 }}></Divider>
            <Box sx={{ p: 3, backgroundColor: 'rgba(242, 243, 245, 0.5)', borderRadius: 1 }}>
              <Stack direction='row' alignItems='center' gap={0.5}>
                <Box component='img' src={orderDetail.logo_url} sx={{ height: 20 }}></Box>
                <Text sx={{ fontWeight: 'bold', fontSize: 16 }}>
                  {orderDetail.app_name} - {formatGoodsDetails(orderDetail?.goods_detail)?.join(', ')}
                </Text>
              </Stack>
              <Stack direction='row' gap={3} justifyContent='space-between' sx={{ mt: 3.5 }}>
                <Stack direction='row' alignItems='center' sx={{ width: '50%' }}>
                  <Text3 sx={{ width: 74 }}>订单号</Text3>
                  <Text sx={{ fontSize: 14 }}>{orderDetail.order_code}</Text>
                </Stack>
                <Stack direction='row' alignItems='center' sx={{ width: '50%' }}>
                  <Text3 sx={{ width: 74 }}>创建时间</Text3>
                  <Text sx={{ fontSize: 14 }}>{orderDetail.gmt_create}</Text>
                </Stack>
              </Stack>
              <Stack direction='row' alignItems='center' sx={{ mt: 3 }}>
                <Text3 sx={{ width: 74 }}>{orderDetail.status === OrderStatusEnum.paid ? '实付款' : '需付款'} </Text3>
                <Text
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: orderDetail.status === OrderStatusEnum.unpaid ? 'error.main' : '#000',
                  }}
                >
                  ￥{' '}
                  {[OrderStatusEnum.paid, OrderStatusEnum.unpaid, OrderStatusEnum.auditing].includes(orderDetail.status)
                    ? orderDetail.real_amount
                    : orderDetail.total_amount}
                </Text>
              </Stack>

              {hasDiscountCoupon && (
                <Stack
                  gap={3}
                  sx={{
                    p: 2,
                    mt: 2,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction='row'>
                    <Text sx={{ width: 98, fontSize: 14 }}>总金额</Text>
                    <Text sx={{ width: 98, fontSize: 14, mr: 15 }}>￥{orderDetail.total_amount}</Text>
                  </Stack>
                  {orderDetail.coupon_code.saved_amount > 0 && (
                    <Stack direction='row'>
                      <Text sx={{ width: 98, fontSize: 14 }}>优惠券</Text>
                      <Text sx={{ width: 98, fontSize: 14, mr: 15, color: 'error.main' }}>
                        -￥{orderDetail.coupon_code.saved_amount.toFixed(2)}
                      </Text>
                      <Text3
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {orderDetail.coupon_code.coupon_code}
                      </Text3>
                    </Stack>
                  )}
                  {orderDetail.referral_code.saved_amount > 0 && (
                    <Stack direction='row'>
                      <Text sx={{ width: 98, fontSize: 14 }}>推荐码</Text>
                      <Text sx={{ width: 98, fontSize: 14, mr: 15, color: 'error.main' }}>
                        -￥{orderDetail.referral_code.saved_amount.toFixed(2)}
                      </Text>
                      <Text3 sx={{ width: 74 }}>{orderDetail.referral_code.referral_code}</Text3>
                    </Stack>
                  )}
                </Stack>
              )}

              {(orderDetail.status === OrderStatusEnum.paid || orderDetail.pay_type === OrderPayTypeEnum.public) &&
                PAY_TYPE_TO_TEXT[orderDetail.pay_type as keyof typeof PAY_TYPE_TO_TEXT] && (
                  <Stack direction='row' alignItems='center' sx={{ mt: 2 }}>
                    <Text3 sx={{ width: 74 }}>付款方式</Text3>

                    <Text sx={{ display: 'flex', alignItems: 'center', fontSize: 14 }}>
                      {PAY_TYPE_TO_TEXT[orderDetail.pay_type as keyof typeof PAY_TYPE_TO_TEXT]}

                      {/* <Box
                    component='img'
                    sx={{ width: 18, height: 18, borderRadius: '50%', ml: 2, mr: 0.5 }}
                    src={defaultAvatar}
                  />
                  9999999 */}
                    </Text>
                    {orderDetail.pay_type === OrderPayTypeEnum.public && (
                      <Text
                        sx={{ fontSize: 14, color: 'primary.main', cursor: 'pointer', ml: 2 }}
                        onClick={() => {
                          setReceiptModalVisible(true)
                        }}
                      >
                        查看对公回执单
                      </Text>
                    )}
                  </Stack>
                )}
            </Box>

            {![OrderStatusEnum.unpaid, OrderStatusEnum.toBeConfirmed].includes(orderDetail.status) && (
              <Stack alignItems='flex-end' sx={{ mt: 5 }}>
                <Button
                  variant='contained'
                  sx={{ width: 120 }}
                  onClick={() => {
                    navigate('/console/space/order')
                  }}
                >
                  返回
                </Button>
              </Stack>
            )}
          </Card>
          {[OrderStatusEnum.unpaid, OrderStatusEnum.toBeConfirmed].includes(orderDetail.status) && (
            <Card sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Text sx={{ fontWeight: 600 }}>
                {orderDetail.status === OrderStatusEnum.unpaid && '选择以下支付方式付款'}
                {orderDetail.status === OrderStatusEnum.toBeConfirmed && '选择使用推荐码 / 优惠券'}
              </Text>
              <Divider sx={{ borderColor: '#eee' }}></Divider>

              {orderDetail.status === OrderStatusEnum.toBeConfirmed && (
                <>
                  <FormGroup row sx={{ gap: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size='small'
                          checked={couponCodeChecked}
                          onChange={(e) => {
                            setCouponCodeChecked(e.target.checked)
                            if (!e.target.checked) {
                              setCouponCodeInfo(null)
                              setCouponCodeError('')
                            }
                          }}
                        />
                      }
                      label='优惠券'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size='small'
                          checked={referralCodeChecked}
                          onChange={(e) => {
                            setReferralCodeChecked(e.target.checked)
                            if (!e.target.checked) {
                              setReferralCodeInfo(null)
                              setReferralCodeError('')
                            }
                          }}
                        />
                      }
                      label='推荐码'
                    />
                  </FormGroup>
                  {/* {couponCodeChecked && (
                    <Stack direction='row' alignItems='center' gap={2}>
                      <TextField
                        label='优惠券'
                        variant='outlined'
                        autoComplete='off'
                        size='small'
                        sx={{
                          width: 408,
                          '.MuiInputBase-input': {
                            fontSize: 14,
                          },
                        }}
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value)
                          debounceCheckCouponCode(e.target.value)
                        }}
                        error={!!couponCodeError}
                      />
                      <Text sx={{ color: 'error.main' }}>
                        {couponCodeMoney
                          ? `立减 ￥${couponCodeMoney} ${
                              couponCodeInfo?.kind === 2 ? `（${Decimal.mul(couponCodeInfo.discount || 0, 10)}折）` : ''
                            }`
                          : ''}
                        {couponCodeError}
                      </Text>
                    </Stack>
                  )} */}

                  {couponCodeChecked && (
                    <Stack direction='row' alignItems='center' gap={2}>
                      <Autocomplete
                        freeSolo
                        getOptionDisabled={(option) => option.threshold > (orderDetail?.total_amount || 0)}
                        options={coupons?.data.filter((item: any) => item.status === 1) || []}
                        sx={{
                          width: 408,
                        }}
                        componentsProps={{
                          paper: {
                            sx: {
                              boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.12)',
                            },
                          },
                        }}
                        onChange={(e, value: any) => {
                          setCouponCode((value?.code as string) || value)
                          debounceCheckCouponCode((value?.code as string) || value)
                        }}
                        getOptionLabel={(option: any) => `${option?.code || option}`}
                        renderOption={(props, option: any) => {
                          const { ...optionProps } = props
                          return (
                            <Stack
                              key={option.code}
                              direction='row'
                              alignItems='center'
                              sx={{ py: 0.5, px: 2 }}
                              component='li'
                              {...optionProps}
                            >
                              <Stack direction='row' sx={{ width: 120, fontWeight: 700, color: 'error.main' }}>
                                {option.kind === 1 ? (
                                  <>
                                    <Box sx={{ fontSize: 14, mt: '15px' }}>￥</Box>
                                    <Box sx={{ fontSize: 28 }}>{option.amount}</Box>
                                  </>
                                ) : (
                                  <>
                                    <Box sx={{ pr: 0.5, fontSize: 28 }}>{option.discount * 10} </Box>
                                    <Box sx={{ mt: '13px' }}>折</Box>
                                  </>
                                )}
                              </Stack>
                              <Stack gap={0.5}>
                                <Box sx={{ fontSize: 14, color: '#000' }}>{option.remark}</Box>
                                <Box sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
                                  {option.threshold === 0 ? '无门槛' : `满 ${option.threshold} 可用`}
                                </Box>
                              </Stack>
                            </Stack>
                          )
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='优惠券'
                            variant='outlined'
                            autoComplete='off'
                            size='small'
                            sx={{
                              width: 408,
                              '.MuiInputBase-input': {
                                fontSize: 14,
                              },
                            }}
                            value={couponCode}
                            onChange={(e) => {
                              setCouponCode(e.target.value)
                              debounceCheckCouponCode(e.target.value)
                            }}
                            error={!!couponCodeError}
                          />
                        )}
                      />
                      <Text sx={{ color: 'error.main' }}>
                        {couponCodeMoney
                          ? `立减 ￥${couponCodeMoney} ${
                              couponCodeInfo?.kind === 2 ? `（${Decimal.mul(couponCodeInfo.discount || 0, 10)}折）` : ''
                            }`
                          : ''}
                        {couponCodeError}
                      </Text>
                    </Stack>
                  )}

                  {referralCodeChecked && (
                    <Stack direction='row' alignItems='center' gap={2}>
                      <TextField
                        label='推荐码'
                        variant='outlined'
                        autoComplete='off'
                        sx={{
                          width: 408,
                          '.MuiInputBase-input': {
                            fontSize: 14,
                          },
                        }}
                        size='small'
                        value={referralCode}
                        onChange={(e) => {
                          setReferralCode(e.target.value)
                          debounceCheckReferralCode(e.target.value)
                        }}
                        error={!!referralCodeError}
                      />
                      <Text sx={{ color: 'error.main' }}>
                        {referralMoney
                          ? `立减 ￥${referralMoney} （${Decimal.mul(referralCodeInfo.discount || 0, 10)}折）`
                          : ''}
                        {referralCodeError}
                      </Text>
                    </Stack>
                  )}
                </>
              )}

              {orderDetail.status === OrderStatusEnum.unpaid && (
                <Stack direction='row' alignItems='center' gap={5}>
                  {PAY_TYPES.map((item) => (
                    <Stack
                      direction='row'
                      gap={1}
                      sx={{
                        width: 160,
                        height: 54,
                        border: '1px solid',
                        borderColor: payType === item.value ? 'primary.main' : '#eee',
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                      justifyContent='center'
                      alignItems='center'
                      key={item.value}
                      onClick={() => setPayType(item.value)}
                    >
                      <Icon type={item.icon} sx={{ fontSize: 24 }}></Icon>
                      <Text sx={{ fontSize: 16 }}>{item.label}</Text>
                    </Stack>
                  ))}
                </Stack>
              )}

              {payType === OrderPayTypeEnum.public && (
                <Stack gap={1}>
                  <Stack direction='row' alignItems='center' sx={{ color: 'rgba(0,0,0,0.5)' }}>
                    <SerialText>1</SerialText>
                    点击查看
                    <Text
                      sx={{ color: 'primary.main', cursor: 'pointer', px: 0.5 }}
                      onClick={() => setPayeeModalVisible(true)}
                    >
                      收款信息
                    </Text>
                  </Stack>
                  <Stack direction='row' alignItems='center' sx={{ color: 'rgba(0,0,0,0.5)' }}>
                    <SerialText>2</SerialText>
                    请填写公司名称
                    <TextField
                      size='small'
                      label='公司名称'
                      placeholder='请填写公司名称'
                      required
                      autoComplete='off'
                      sx={{ ml: 2, width: 300 }}
                      value={company}
                      onChange={(e) => {
                        setCompany(e.target.value)
                      }}
                      error={!!companyError}
                      helperText={companyError}
                    />
                  </Stack>
                  <Stack direction='row' alignItems='center' sx={{ color: 'rgba(0,0,0,0.5)' }}>
                    <SerialText>3</SerialText>
                    转账时，请在备注信息中填入
                    <Text sx={{ fontWeight: 600 }}>订单号"{searchParams.get('order_code')}"</Text>
                  </Stack>
                  <Stack direction='row' alignItems='center' sx={{ color: 'rgba(0,0,0,0.5)' }}>
                    <SerialText>4</SerialText>
                    转账完成后，点击下方
                    <Text sx={{ fontWeight: 600 }}>上传回执单</Text>
                  </Stack>
                </Stack>
              )}

              {orderDetail.status === OrderStatusEnum.toBeConfirmed && (
                <Stack direction='row' alignItems='center' justifyContent='flex-end'>
                  <Checkbox
                    sx={{ color: 'rgba(0,0,0,0.5)' }}
                    checked={isAgree}
                    size='small'
                    onChange={(e) => {
                      setIsAgree(e.target.checked)
                    }}
                  />
                  <Text
                    sx={{
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontSize: 14,
                    }}
                    onClick={() => {
                      setIsAgree(true)
                      window.open('/service-agreement.html', '_blank')
                    }}
                  >
                    《长亭百川云平台产品服务协议》
                  </Text>
                </Stack>
              )}

              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' sx={{ color: 'error.main', alignItems: 'flex-end', gap: 1, fontSize: 16 }}>
                  <Box>￥</Box>
                  {orderDetail.status === OrderStatusEnum.toBeConfirmed && (
                    <>
                      <Box sx={{ fontSize: 26 }}>{payMoney}</Box>
                      {(couponCodeMoney > 0 || +referralMoney > 0) && (
                        <Box
                          sx={(theme) => ({
                            color: alpha(theme.palette.error.main, 0.5),
                          })}
                        >
                          = {(orderDetail.total_amount || 0).toFixed(2)}
                          {couponCodeMoney ? ` - ${couponCodeMoney}` : ''}
                          {referralMoney ? ` - ${referralMoney}` : ''}{' '}
                        </Box>
                      )}
                    </>
                  )}
                  {orderDetail.status === OrderStatusEnum.unpaid && (
                    <>
                      <Box sx={{ fontSize: 26 }}>{orderDetail.real_amount.toFixed(2)}</Box>
                      {(orderDetail.referral_code?.referral_code || orderDetail.coupon_code?.coupon_code) && (
                        <Box
                          sx={(theme) => ({
                            color: alpha(theme.palette.error.main, 0.5),
                          })}
                        >
                          = {(orderDetail.total_amount || 0).toFixed(2)}
                          {orderDetail.coupon_code?.saved_amount
                            ? ` - ${orderDetail.coupon_code?.saved_amount.toFixed(2)}`
                            : ''}
                          {orderDetail.referral_code?.saved_amount
                            ? ` - ${orderDetail.referral_code?.saved_amount.toFixed(2)}`
                            : ''}
                        </Box>
                      )}
                    </>
                  )}
                </Stack>

                <Stack direction='row' gap={3}>
                  <Button onClick={onCancelOrder}>关闭订单</Button>
                  {OrderPayTypeEnum.alipay === payType && (
                    <Button variant='contained' onClick={onConfirmOrder}>
                      {OrderStatusEnum.toBeConfirmed === orderDetail.status && '确认订单'}
                      {OrderStatusEnum.unpaid === orderDetail.status && '确认支付'}
                    </Button>
                  )}
                  {OrderPayTypeEnum.public === payType && (
                    <LoadingButton
                      loading={payLoading}
                      variant='contained'
                      component='label'
                      role={undefined}
                      tabIndex={-1}
                      disabled={company.trim() === ''}
                    >
                      <VisuallyHiddenInput type='file' accept='image/*' onChange={onImageChange} />
                      上传回执单
                    </LoadingButton>
                  )}
                </Stack>
              </Stack>
            </Card>
          )}
        </Stack>
      </Box>
    </ThemeProvider>
  )
}

export default OrderPay
